import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer
} from 'components/Filters'
import { Select } from 'components/v2/Select'
import { Tag } from 'components/v2/Tag'
import { Input } from 'components/Input'
import { GTBusinessPartnerAutocomplete, GTDriverSuggestion } from 'gql/types.generated'
import { EDriverAssignedStatus, EDriverStatus, IDriversFiltersForm } from 'features/Drivers/types'
import { PartnerAutocomplete } from 'features/Partners/components/PartnerAutocomplete'
import { DriverAutocomplete } from 'features/Drivers/components/DriverAutocomplete'

const messages = defineMessages({
  selectStatus: {
    id: 'businessDriver.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  emailPlaceholder: {
    id: 'businessDriver.filters.email.placeholder',
    defaultMessage: 'Enter an email'
  },
  namePlaceholder: {
    id: 'businessDriver.filters.name.placeholder',
    defaultMessage: 'Enter a name'
  },
  partnerPlaceholder: {
    id: 'businessDriver.filters.kam.placeholder',
    defaultMessage: 'Select Partner'
  },
  assignedStatus: {
    id: 'businessDriver.filters.assigned',
    defaultMessage: 'Assigned'
  },
  unassignedStatus: {
    id: 'businessDriver.filters.unassigned',
    defaultMessage: 'Unassigned'
  },
  activeStatus: {
    id: 'businessDriver.filters.active',
    defaultMessage: 'Active'
  },
  inactiveStatus: {
    id: 'businessDriver.filters.inactive',
    defaultMessage: 'Inactive'
  }
})

const defaultValues: IDriversFiltersForm = {
  driver: {
    id: '',
    name: ''
  },
  email: '',
  phoneNumber: '',
  status: '',
  partner: {
    id: '',
    name: ''
  },
  assigned: ''
}

interface IDriversFiltersProps {
  filters: IDriversFiltersForm
  onSubmit: (values: IDriversFiltersForm) => void
}

export const DriversFilters = ({ filters, onSubmit }: IDriversFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const initialValues: IDriversFiltersForm = {
    driver: {
      id: filters.driver?.id || '',
      name: filters.driver?.name || ''
    },
    status: filters.status || '',
    partner: filters.partner || defaultValues.partner,
    assigned: filters.assigned || '',
    email: filters.email || '',
    phoneNumber: filters.phoneNumber || ''
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
          const {
            target: { id, value }
          } = event

          handleChange(event)
          onSubmit({
            ...values,
            [id]: value
          })
        }
        const handlePartnerChange = (partner: GTBusinessPartnerAutocomplete): void => {
          setFieldValue('partner', partner)
          onSubmit({
            ...values,
            partner
          })
        }

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null
                      switch (filterKey) {
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value =
                            filterValue === EDriverStatus.Active ? (
                              <FormattedMessage id="dictionary.active" defaultMessage="Active" />
                            ) : (
                              <FormattedMessage id="dictionary.inactive" defaultMessage="Inactive" />
                            )
                          break
                        case 'driver':
                          label = <FormattedMessage id="businessDriver.filters.name.label" defaultMessage="Name" />
                          value = (filterValue as GTDriverSuggestion).name
                          break
                        case 'email':
                          label = <FormattedMessage id="businessCustomer.email.label" defaultMessage="Email" />
                          value = filterValue
                          break
                        case 'phoneNumber':
                          label = <FormattedMessage id="businessCustomer.phone.label" defaultMessage="Phone" />
                          value = filterValue
                          break
                        case 'partner':
                          label = (
                            <FormattedMessage id="businessDriver.filters.partner.label" defaultMessage="Partner" />
                          )
                          value = (filterValue as GTBusinessPartnerAutocomplete).name
                          break
                        case 'assigned':
                          label = (
                            <FormattedMessage id="businessDriver.filters.assigned.label" defaultMessage="Assigned" />
                          )
                          value =
                            filterValue === EDriverAssignedStatus.Assigned ? (
                              <FormattedMessage id="dictionary.assigned" defaultMessage="Assigned" />
                            ) : (
                              <FormattedMessage id="dictionary.notAssigned" defaultMessage="Not Assigned" />
                            )
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <DriverAutocomplete
                    placeholder={intl.formatMessage(messages.namePlaceholder)}
                    value={values.driver.name}
                    label={<FormattedMessage id="businessDriver.name.label" defaultMessage="Driver name" />}
                    onChange={(driver): void => {
                      setFieldValue('driver', {
                        id: driver.id,
                        name: driver.name
                      })
                      onSubmit({
                        ...values,
                        driver: {
                          id: driver.id,
                          name: driver.name
                        }
                      })
                    }}
                  />
                  <Input
                    id="email"
                    onChange={handleChange}
                    value={values.email}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    label={<FormattedMessage id="businessDriver.email.label" defaultMessage="Email" />}
                  />
                  <Input
                    id="phoneNumber"
                    onChange={handleChange}
                    value={values.phoneNumber}
                    placeholder={intl.formatMessage({
                      id: 'businessDriver.filters.phone.placeholder',
                      defaultMessage: 'Enter a phone'
                    })}
                    label={<FormattedMessage id="businessDriver.phone.label" defaultMessage="Phone" />}
                  />
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>
                    <option value={EDriverStatus.Active}>
                      <FormattedMessage id="dictionary.active" defaultMessage="Active" />
                    </option>
                    <option value={EDriverStatus.Inactive}>
                      <FormattedMessage id="dictionary.inactive" defaultMessage="Inactive" />
                    </option>
                  </Select>
                  <Select
                    id="assigned"
                    value={values.assigned}
                    onChange={handleSelectChange}
                    label={
                      <FormattedMessage id="businessDriver.filters.assignedStatus" defaultMessage="Assigned Status" />
                    }
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>
                    <option value={EDriverAssignedStatus.Assigned}>
                      <FormattedMessage id="dictionary.assigned" defaultMessage="Assigned" />
                    </option>
                    <option value={EDriverAssignedStatus.Unassigned}>
                      <FormattedMessage id="dictionary.notAssigned" defaultMessage="Not Assigned" />
                    </option>
                  </Select>
                  <PartnerAutocomplete
                    placeholder={intl.formatMessage(messages.partnerPlaceholder)}
                    value={values.partner.name}
                    label={<FormattedMessage id="businessDriver.filters.partner.label" defaultMessage="Partner" />}
                    onChange={handlePartnerChange}
                  />
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <button type="submit" hidden>
              <FormattedMessage id="businessDriver.filters.submit" defaultMessage="Submit" />
            </button>
          </form>
        )
      }}
    </Formik>
  )
}
